/**
 * Internationalization (i18n) configuration module.
 * Sets up multi-language support for the application using i18next.
 */

import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

/**
 * Loads translation files for a specific language.
 * @param {string} language - The language code (e.g., 'fi' for Finnish, 'en' for English)
 * @returns {Promise<Object>} A promise that resolves to the translation object
 * @throws {Error} If the translation file cannot be loaded
 */
const loadTranslations = async (language) => {
  try {
    const response = await fetch(`/locales/${language}/translation.json`);
    const translations = await response.json();
    return translations;
  } catch (error) {
    console.error(`Error loading ${language} translations:`, error);
    return {};
  }
};

/**
 * Initializes the i18n instance with translations for all supported languages.
 * Sets up language detection and fallback options.
 * @returns {Promise<i18n>} A promise that resolves to the initialized i18n instance
 * @throws {Error} If initialization fails
 */
const initI18n = async () => {
  try {
    const [fiTranslations, enTranslations] = await Promise.all([
      loadTranslations("fi"),
      loadTranslations("en"),
    ]);

    await i18n
      .use(initReactI18next)
      .use(LanguageDetector)
      .init({
        resources: {
          fi: {
            translation: fiTranslations,
          },
          en: {
            translation: enTranslations,
          },
        },
        lng: "fi", // set Finnish as default language
        fallbackLng: "en",
        detection: {
          order: ["localStorage", "navigator"],
          caches: ["localStorage"],
        },
        interpolation: {
          escapeValue: false,
        },
      });
    return i18n;
  } catch (error) {
    console.error("Error initializing i18n:", error);
  }
};

// Initialize i18n with basic configuration first
i18n
  .use(initReactI18next)
  .use(LanguageDetector)
  .init({
    resources: {
      fi: {
        translation: {},
      },
      en: {
        translation: {},
      },
    },
    lng: "fi",
    fallbackLng: "en",
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
    },
    interpolation: {
      escapeValue: false,
    },
  });

// Load full translations
initI18n().catch(console.error);

export default i18n;
